import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Archive as ArchiveIcon,
  Visibility as ViewIcon,
  History as HistoryIcon,
  FileDownload as DownloadIcon,
  ImageOutlined as ImageOutlinedIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { parsePlatformContent } from '../content/utils/contentParser';
import { PlatformIcon } from '../../components/features/content/PlatformIcon';
import { VariationCard } from '../../components/features/content/VariationCard';
import { hexToRgba } from '../content/utils/colorUtils';
import { getPlatformDisplayName } from '../../utils/platformUtils';

interface HistoryCardProps {
  item: any;
  copiedStates: { [key: string]: boolean };
  onCopy: (text: string, key: string) => void;
  onArchive: (id: number) => void;
  onView: (id: number) => void;
  platformDisplayNameMap?: Record<string, string>;
  arePlatformsExpanded?: boolean;
  businessProfileMap?: Record<number, any>;
  purposeNameToDisplayNameMap?: Record<string, string>;
}

const HistoryCard: React.FC<HistoryCardProps> = ({
  item,
  copiedStates,
  onCopy,
  onArchive,
  onView,
  platformDisplayNameMap = {},
  arePlatformsExpanded = false,
  businessProfileMap = {},
  purposeNameToDisplayNameMap = {},
}) => {
  const { t, i18n } = useTranslation('content');
  const [platformExpanded, setPlatformExpanded] = useState<{ [key: string]: boolean }>({});
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const handleDownload = async (url: string) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = 'bizcontent-image.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(url, '_blank');
    }
  };
  
  // Sync with parent control: if arePlatformsExpanded changes, update all platforms
  useEffect(() => {
    if (arePlatformsExpanded !== undefined) {
      const newState: { [key: string]: boolean } = {};
      if (item.generated_content && typeof item.generated_content === 'object') {
        Object.keys(item.generated_content).forEach((platformName: string) => {
          newState[platformName] = arePlatformsExpanded;
        });
      }
      setPlatformExpanded(newState);
    }
  }, [arePlatformsExpanded, item.generated_content]);

  // Helper function to get business name
  const getBusinessName = (): string => {
    let businessName = item.business_profile?.business_name;
    if (!businessName && item.business_profile_id && businessProfileMap[item.business_profile_id]) {
      businessName = businessProfileMap[item.business_profile_id].business_name;
    }
    return businessName || 'Unknown Business';
  };

  // Helper function to get purpose name (raw value)
  const getPurposeName = (): string | null => {
    let purposeName: string | null = null;
    if (typeof item.content_purpose === 'string') {
      purposeName = item.content_purpose;
    } else if (item.content_purpose && typeof item.content_purpose === 'object') {
      purposeName = item.content_purpose.name || item.content_purpose.display_name;
    } else if (item.generation_metadata && item.generation_metadata.content_purpose) {
      purposeName = item.generation_metadata.content_purpose;
    }
    return purposeName;
  };

  // Helper function to get translated purpose display name
  const getTranslatedPurposeDisplayName = (): string | null => {
    const purposeName = getPurposeName();
    if (!purposeName) return null;
    
    // Convert purpose name to translation key (e.g., "Product Launch" -> "product_launch")
    const translationKey = purposeName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    // Try to get translated name, fallback to purposeNameToDisplayNameMap, then raw name
    const translatedName = t(`purposes.${translationKey}`, { defaultValue: '' });
    if (translatedName && translatedName !== `purposes.${translationKey}`) {
      return translatedName;
    }
    return purposeNameToDisplayNameMap[purposeName] || purposeName;
  };

  // Get localized date string based on current language
  const getLocalizedDate = (): string => {
    const date = new Date(item.created_at);
    const locale = i18n.language === 'fr' ? 'fr-FR' : i18n.language === 'es' ? 'es-ES' : 'en-US';
    return date.toLocaleDateString(locale, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const accordionSx = {
    mb: 1.5,
    borderRadius: '8px',
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    backgroundColor: hexToRgba('#6C63FF', 0.08),
    borderLeft: '4px solid #6C63FF',
    overflow: 'hidden',
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: '0 0 12px 0' },
  };

  const summarySx = {
    px: 1.5,
    py: 0.1,
    borderRadius: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    minHeight: '36px',
    '&:hover': { backgroundColor: hexToRgba('#6C63FF', 0.12) },
    '&.Mui-expanded': { 
      borderBottom: '1px solid', 
      borderColor: 'divider', 
      mb: 1,
      borderRadius: '8px 8px 0 0',
    },
    '&:not(.Mui-expanded)': {
      borderRadius: '8px',
    },
  };

  const contextBoxSx = {
    p: 1.5,
    mb: 1.5,
    backgroundColor: 'grey.50',
    border: '1px solid',
    borderColor: 'grey.200',
    borderRadius: '8px',
  };

  const renderPlatformContent = (platformName: string, content: any) => {
    const displayName = getPlatformDisplayName(platformDisplayNameMap[platformName] || platformName);
    const isExpanded = platformExpanded[platformName] || false;
    const platformKey = platformName.toLowerCase();
    const parsed = parsePlatformContent(content, platformKey);
    const { context: platformContext, variations } = parsed;
    
    return (
      <Accordion 
        key={platformName} 
        expanded={isExpanded}
        onChange={(_, expanded) => {
          setPlatformExpanded(prev => ({ ...prev, [platformName]: expanded }));
        }}
        sx={accordionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />} sx={summarySx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
            <PlatformIcon platformName={platformName} size={20} marginRight={0} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.9rem' }}>
              {displayName}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1.5, pt: 1.5, pb: 1.5, borderRadius: '0 0 8px 8px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {platformContext && (
              <Box sx={contextBoxSx}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'text.secondary' }}>
                  {platformContext}
                </Typography>
              </Box>
            )}
            {variations.length > 0 ? (
              variations.map((variation) => (
                <VariationCard
                  key={variation.key}
                  variation={variation.data}
                  variationKey={variation.key}
                  platformKey={platformKey}
                  variationIndex={variation.index}
                  copiedStates={copiedStates}
                  onCopy={onCopy}
                  compact={true}
                />
              ))
            ) : (
              <Box sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, backgroundColor: 'background.paper', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">{t('history.noContent', { defaultValue: 'No content available' })}</Typography>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const cardSx = { mb: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider', borderRadius: '8px', backgroundColor: 'background.paper', overflow: 'hidden' };
  const headerSx = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pb: 1.5, borderBottom: '2px solid', borderColor: 'divider' };
  const emptyStateSx = { p: 2, backgroundColor: 'grey.50', borderRadius: '8px', border: '1px solid', borderColor: 'grey.300', textAlign: 'center' };

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={headerSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <HistoryIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{getBusinessName()}</Typography>
              {getTranslatedPurposeDisplayName() && (
                <Typography variant="body2" sx={{ mb: 0.5, color: 'primary.main' }}>
                  {t('history.purpose', { defaultValue: 'Purpose' })}: {getTranslatedPurposeDisplayName()}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">{getLocalizedDate()}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={t('history.actions.view')}>
              <IconButton onClick={() => onView(item.id)}><ViewIcon /></IconButton>
            </Tooltip>
            <Tooltip title={t('history.actions.archive')}>
              <IconButton 
                onClick={() => onArchive(item.id)} 
                sx={{ 
                  color: '#6C63FF',
                  '&:hover': { 
                    backgroundColor: 'rgba(108, 99, 255, 0.1)',
                    color: '#5a52ee'
                  }
                }}
              >
                <ArchiveIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {/* AI Generated Image thumbnail */}
        {item.image_status === 'success' && item.image_url && (
          <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ImageOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                {t('generation.image.cardTitle')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="img"
                src={item.image_url}
                alt={t('generation.image.cardTitle')}
                onClick={() => setLightboxOpen(true)}
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 1,
                  objectFit: 'cover',
                  flexShrink: 0,
                  cursor: 'pointer',
                  transition: 'opacity 0.15s ease',
                  '&:hover': { opacity: 0.85 },
                }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownload(item.image_url)}
              >
                {t('generation.image.download')}
              </Button>
            </Box>
          </Box>
        )}

        {/* Lightbox */}
        <Dialog
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          maxWidth="md"
          PaperProps={{ sx: { backgroundColor: 'transparent', boxShadow: 'none', overflow: 'visible' } }}
        >
          <Box sx={{ position: 'relative' }}>
            <IconButton
              onClick={() => setLightboxOpen(false)}
              sx={{
                position: 'absolute',
                top: -16,
                right: -16,
                backgroundColor: 'white',
                boxShadow: 2,
                zIndex: 1,
                '&:hover': { backgroundColor: 'grey.100' },
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box
              component="img"
              src={item.image_url}
              alt={t('generation.image.cardTitle')}
              sx={{ display: 'block', maxWidth: '90vw', maxHeight: '85vh', borderRadius: 2, objectFit: 'contain' }}
            />
          </Box>
        </Dialog>

        {item.generated_content && typeof item.generated_content === 'object' ? (
          <Box sx={{ mt: 1 }}>
            {Object.entries(item.generated_content).map(([platform, content]) => renderPlatformContent(platform, content))}
          </Box>
        ) : (
          <Box sx={emptyStateSx}>
            <Typography variant="body2" color="text.secondary">{t('history.noContentGenerated', { defaultValue: 'No content generated' })}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default HistoryCard;
